import React from 'react';

const OVERFLOW_ANCHOR_LOCK_COUNT_ATTR = 'data-overflow-anchor-lock-count';
const OVERFLOW_ANCHOR_PREV_ATTR = 'data-overflow-anchor-prev-inline';

const findNearestScrollContainer = (start: HTMLElement): HTMLElement | null => {
  let node: HTMLElement | null = start.parentElement;
  while (node) {
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY;
    const isScrollable = /(auto|scroll|overlay)/.test(overflowY) && node.scrollHeight > node.clientHeight + 1;
    if (isScrollable) {
      return node;
    }
    node = node.parentElement;
  }
  return (document.scrollingElement as HTMLElement | null) ?? null;
};

export const useOverflowAnchorLock = (rootRef: React.RefObject<HTMLElement | null>) => {
  React.useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof window === 'undefined') return;

    const container = findNearestScrollContainer(root);
    if (!container) return;

    const existingCount = Number(container.getAttribute(OVERFLOW_ANCHOR_LOCK_COUNT_ATTR) || '0');
    if (existingCount === 0) {
      container.setAttribute(OVERFLOW_ANCHOR_PREV_ATTR, container.style.overflowAnchor || '');
      container.style.overflowAnchor = 'none';
    }
    container.setAttribute(OVERFLOW_ANCHOR_LOCK_COUNT_ATTR, String(existingCount + 1));

    return () => {
      const currentCount = Number(container.getAttribute(OVERFLOW_ANCHOR_LOCK_COUNT_ATTR) || '1');
      const nextCount = currentCount - 1;

      if (nextCount <= 0) {
        const previousInline = container.getAttribute(OVERFLOW_ANCHOR_PREV_ATTR) || '';
        if (previousInline) {
          container.style.overflowAnchor = previousInline;
        } else {
          container.style.removeProperty('overflow-anchor');
        }
        container.removeAttribute(OVERFLOW_ANCHOR_LOCK_COUNT_ATTR);
        container.removeAttribute(OVERFLOW_ANCHOR_PREV_ATTR);
      } else {
        container.setAttribute(OVERFLOW_ANCHOR_LOCK_COUNT_ATTR, String(nextCount));
      }
    };
  }, [rootRef]);
};
